import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../lib/api';

export interface BootstrapJob {
  id: string;
  document_id: string;
  status: 'running' | 'succeeded' | 'failed';
  step: 'generating' | 'saving' | 'done';
  sections_total: number;
  sections_created: number;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  typical_duration_seconds: number;
}

const POLL_MS = 2_000;
/* Le succès s'efface tout seul : les sections viennent d'apparaître dans le
   document, elles sont la vraie preuve. Une carte verte qui reste ajoute du
   bruit à un écran déjà chargé. */
const SUCCESS_NOTICE_MS = 10_000;

/** Suit le bootstrap de fond d'un document : à l'ouverture, puis en boucle
 *  courte tant qu'il tourne. C'est cette relecture à l'ouverture qui fait
 *  qu'un enseignant parti retrouve son travail au lieu d'un document vide. */
export function useBootstrapJob(documentId: string, onSucceeded: () => void) {
  const [job, setJob] = useState<BootstrapJob | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const succeededRef = useRef(onSucceeded);
  succeededRef.current = onSucceeded;
  const previousStatus = useRef<BootstrapJob['status'] | null>(null);

  const refresh = useCallback(async () => {
    try {
      setJob((await api.content.bootstrap.status.query({ documentId })) as BootstrapJob | null);
    } catch {
      /* Un sondage perdu n'efface pas ce que l'écran montre déjà : la panne du
         sondage n'est pas la panne du travail. */
    }
  }, [documentId]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (job?.status !== 'running') return;
    const timer = window.setInterval(() => { void refresh(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [job?.status, refresh]);

  useEffect(() => {
    const status = job?.status ?? null;
    if (previousStatus.current === 'running' && status === 'succeeded') {
      succeededRef.current();
      setShowSuccess(true);
      const timer = window.setTimeout(() => setShowSuccess(false), SUCCESS_NOTICE_MS);
      previousStatus.current = status;
      return () => window.clearTimeout(timer);
    }
    previousStatus.current = status;
  }, [job?.status]);

  const retry = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    setRetryError(null);
    try {
      setJob((await api.content.bootstrap.retry.mutate({ documentId })) as BootstrapJob);
    } catch (e) {
      setRetryError(e instanceof Error ? e.message : String(e));
    } finally {
      setRetrying(false);
    }
  }, [documentId, retrying]);

  return { job, showSuccess, retry, retrying, retryError, refresh };
}
